import { jsx, jsxs } from 'react/jsx-runtime';
import { useMemo, useEffect, useRef, useState } from 'react';
import { H2, CarouselBeta, CarouselSlideBeta } from '@zooplus/ui-components';
import useAdobeUtils from '../../hooks/use-adobe-utils.js';
import { SentryErrorBoundary } from '../error-boundary/SentryErrorBoundary.js';
import { RecommendationsProductCard } from '../recommendations-product-card/RecommendationsProductCard.js';
import { RecommendationAdapter } from '../utils.js';
import styles from './RecommendationsCarousel.module.scss.js';

function calculateMaxHeight(nodes) {
    return nodes.reduce((maxHeight, node) => {
        if (node) {
            return Math.max(maxHeight, node.offsetHeight);
        }
        return maxHeight;
    }, 0);
}
function getCssAttributes(height) {
    return { height: height ? `${height}px` : 'auto' };
}
function getStyleProps(maxSponsoredBadgeHeight, maxTitleHeight, maxSubtitleHeight, maxTagBoxHeight, maxPriceHeight) {
    return {
        sponsoredBadgeStyle: getCssAttributes(maxSponsoredBadgeHeight),
        titleStyle: getCssAttributes(maxTitleHeight),
        subtitleStyle: getCssAttributes(maxSubtitleHeight),
        tagBoxStyle: getCssAttributes(maxTagBoxHeight),
        priceStyle: getCssAttributes(maxPriceHeight),
    };
}
const RecommendationsCarousel = ({ className = '', caption, emblaOptions, recommendations, addToCartButton, priceVersion = 'old', ...otherProps }) => {
    const { trackSponsoredImpressionEvent } = useAdobeUtils();
    const recommendationObjects = useMemo(() => (recommendations ?? []).map((reco) => new RecommendationAdapter(reco)), [recommendations]);
    //ToDo: Add FallbackComponent if recommendations is empty RE-3265
    const sponsoredRecos = useMemo(() => recommendationObjects.filter((recoObj) => recoObj.getSponsored()), [recommendationObjects]);
    // Track impressions of sponsored items.
    useEffect(() => {
        if (sponsoredRecos.length > 0) {
            trackSponsoredImpressionEvent(sponsoredRecos);
        }
    }, [sponsoredRecos, trackSponsoredImpressionEvent]);
    const references = {
        sponsoredBadgeRefs: useRef(new Map()),
        titleRefs: useRef(new Map()),
        subtitleRefs: useRef(new Map()),
        tagBoxRefs: useRef(new Map()),
        priceRefs: useRef(new Map()),
    };
    const [maxSponsoredBadgeHeight, setMaxSponsoredBadgeHeight] = useState();
    const [maxTitleHeight, setMaxTitleHeight] = useState();
    const [maxSubtitleHeight, setMaxSubtitleHeight] = useState();
    const [maxTagBoxHeight, setMaxTagBoxHeight] = useState();
    const [maxPriceHeight, setMaxPriceHeight] = useState();
    const [isMapRefSet, setIsMapRefSet] = useState(false);
    const [hasMeasured, setHasMeasured] = useState(false);
    useEffect(() => {
        if (isMapRefSet) {
            // @typescript-eslint/no-unused-vars
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const sponsoredBadgeNodes = Array.from(references.sponsoredBadgeRefs.current).map(([_, value]) => value);
            // @typescript-eslint/no-unused-vars
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const titleNodes = Array.from(references.titleRefs.current).map(([_, value]) => value);
            // @typescript-eslint/no-unused-vars
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const subtitleNodes = Array.from(references.subtitleRefs.current).map(([_, value]) => value);
            // @typescript-eslint/no-unused-vars
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const tagBoxNodes = Array.from(references.tagBoxRefs.current).map(([_, value]) => value);
            // @typescript-eslint/no-unused-vars
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const priceNodes = Array.from(references.priceRefs.current).map(([_, value]) => value);
            setMaxSponsoredBadgeHeight(calculateMaxHeight(sponsoredBadgeNodes));
            setMaxTitleHeight(calculateMaxHeight(titleNodes));
            setMaxSubtitleHeight(calculateMaxHeight(subtitleNodes));
            setMaxTagBoxHeight(calculateMaxHeight(tagBoxNodes));
            setMaxPriceHeight(calculateMaxHeight(priceNodes));
            setHasMeasured(true);
        }
    }, [isMapRefSet]);
    const styleProps = useMemo(() => {
        return getStyleProps(maxSponsoredBadgeHeight, maxTitleHeight, maxSubtitleHeight, maxTagBoxHeight, maxPriceHeight);
    }, [maxSponsoredBadgeHeight, maxTitleHeight, maxSubtitleHeight, maxTagBoxHeight, maxPriceHeight]);
    return (jsx("div", { style: { visibility: hasMeasured ? 'visible' : 'hidden' }, children: jsx(SentryErrorBoundary, { children: jsxs("div", { className: `${styles.recommendationsCarousel} ${className}`, ...otherProps, children: [jsx(H2, { className: `${styles.recoCarouselHeader}`, children: caption }), jsx(CarouselBeta, { className: styles.carousel, emblaOptions: emblaOptions, gap: "12px", id: "story-product-recos", nextButtonAriaLabel: "next", previousButtonAriaLabel: "previous", children: recommendationObjects.map((recommendation, index) => {
                            return (jsx(CarouselSlideBeta, { className: styles.carouselSlide, children: jsx(RecommendationsProductCard, { recommendationAttributes: recommendation, addToCartButton: addToCartButton, priceVersion: priceVersion, references: references, styleProps: styleProps, index: index, setIsMapRefSet: setIsMapRefSet }) }, index));
                        }) })] }) }) }));
};

export { RecommendationsCarousel };
//# sourceMappingURL=RecommendationsCarousel.js.map
